/**
 * Form Field Editor Component
 *
 * A component for editing individual survey form fields/questions.
 * Supports various question types and validation options.
 *
 * @example
 * ```tsx
 * <FormFieldEditor
 *   field={question}
 *   onChange={handleFieldChange}
 *   onDelete={handleFieldDelete}
 *   onMoveUp={handleMoveUp}
 *   onMoveDown={handleMoveDown}
 * />
 * ```
 */

import { useState, useCallback } from 'react';
import type { SurveyQuestion, QuestionType } from '@/types';

/**
 * Props for the FormFieldEditor component
 */
interface FormFieldEditorProps {
  /** The field/question to edit */
  field: SurveyQuestion;
  /** Index of the field in the form */
  index: number;
  /** Total number of fields */
  totalFields: number;
  /** Callback when the field changes */
  onChange: (field: SurveyQuestion) => void;
  /** Callback to delete the field */
  onDelete: () => void;
  /** Callback to move field up */
  onMoveUp: () => void;
  /** Callback to move field down */
  onMoveDown: () => void;
}

/**
 * Question type options for the dropdown
 */
const QUESTION_TYPES: { value: QuestionType; label: string; icon: string }[] = [
  { value: 'text', label: 'Short Text', icon: 'T' },
  { value: 'textarea', label: 'Long Text', icon: '=' },
  { value: 'number', label: 'Number', icon: '#' },
  { value: 'date', label: 'Date', icon: 'D' },
  { value: 'select', label: 'Dropdown', icon: 'v' },
  { value: 'multiselect', label: 'Multi-Select', icon: '[]' },
  { value: 'radio', label: 'Radio Buttons', icon: 'O' },
  { value: 'checkbox', label: 'Checkboxes', icon: '[]' },
];

/**
 * Check if the question type supports options
 */
function supportsOptions(type: QuestionType): boolean {
  return ['select', 'multiselect', 'radio', 'checkbox'].includes(type);
}

/**
 * Check if the question type supports min/max validation
 */
function supportsMinMax(type: QuestionType): boolean {
  return ['text', 'textarea', 'number'].includes(type);
}

/**
 * Form Field Editor Component
 */
export function FormFieldEditor({
  field,
  index,
  totalFields,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}: FormFieldEditorProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [newOption, setNewOption] = useState('');

  /**
   * Handle field property change
   */
  const handleChange = useCallback(
    <K extends keyof SurveyQuestion>(key: K, value: SurveyQuestion[K]) => {
      onChange({ ...field, [key]: value });
    },
    [field, onChange]
  );

  /**
   * Handle type change - reset options if switching from/to option-based type
   */
  const handleTypeChange = useCallback(
    (newType: QuestionType) => {
      const updatedField: SurveyQuestion = {
        ...field,
        type: newType,
      };

      // Add default options if switching to an option-based type
      if (supportsOptions(newType) && (!field.options || field.options.length === 0)) {
        updatedField.options = ['Option 1', 'Option 2'];
      }

      // Clear options if switching away from option-based type
      if (!supportsOptions(newType)) {
        updatedField.options = undefined;
      }

      // Clear validation if switching to non-min/max type
      if (!supportsMinMax(newType)) {
        updatedField.validation = undefined;
      }

      onChange(updatedField);
    },
    [field, onChange]
  );

  /**
   * Handle adding a new option
   */
  const handleAddOption = useCallback(() => {
    if (!newOption.trim()) return;

    const currentOptions = field.options || [];
    onChange({
      ...field,
      options: [...currentOptions, newOption.trim()],
    });
    setNewOption('');
  }, [field, newOption, onChange]);

  /**
   * Handle removing an option
   */
  const handleRemoveOption = useCallback(
    (optionIndex: number) => {
      const currentOptions = field.options || [];
      onChange({
        ...field,
        options: currentOptions.filter((_, i) => i !== optionIndex),
      });
    },
    [field, onChange]
  );

  /**
   * Handle updating an option
   */
  const handleUpdateOption = useCallback(
    (optionIndex: number, value: string) => {
      const currentOptions = field.options || [];
      onChange({
        ...field,
        options: currentOptions.map((opt, i) => (i === optionIndex ? value : opt)),
      });
    },
    [field, onChange]
  );

  /**
   * Handle validation change
   */
  const handleValidationChange = useCallback(
    (key: 'min' | 'max' | 'pattern', value: string) => {
      const currentValidation = field.validation || {};
      const numValue = key !== 'pattern' ? (value ? Number(value) : undefined) : undefined;
      const strValue = key === 'pattern' ? (value || undefined) : undefined;

      onChange({
        ...field,
        validation: {
          ...currentValidation,
          [key]: numValue ?? strValue,
        },
      });
    },
    [field, onChange]
  );

  return (
    <div className="border border-white/10 rounded-lg bg-white/5 overflow-hidden">
      {/* Header - Always Visible */}
      <div className="flex items-center gap-3 p-4 bg-white/5">
        {/* Question Number */}
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/20 text-primary text-sm font-medium">
          {index + 1}
        </div>

        {/* Question Label Preview */}
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">
            {field.label || 'Untitled Question'}
          </p>
          <p className="text-xs text-white/40">
            {QUESTION_TYPES.find((t) => t.value === field.type)?.label || field.type}
            {field.required && <span className="text-primary ml-1">*</span>}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {/* Move Up */}
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move up"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>

          {/* Move Down */}
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === totalFields - 1}
            className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move down"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Expand/Collapse */}
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded transition-colors"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg
              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Delete */}
          <button
            type="button"
            onClick={onDelete}
            className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
            title="Delete question"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 border-t border-white/10">
          {/* Question Type */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Question Type</label>
            <select
              value={field.type}
              onChange={(e) => handleTypeChange(e.target.value as QuestionType)}
              className="input"
            >
              {QUESTION_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Question Label */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Question Label <span className="text-primary">*</span>
            </label>
            <input
              type="text"
              value={field.label}
              onChange={(e) => handleChange('label', e.target.value)}
              className="input"
              placeholder="Enter your question..."
            />
          </div>

          {/* Question Description */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Description <span className="text-white/40">(optional)</span>
            </label>
            <input
              type="text"
              value={field.description || ''}
              onChange={(e) => handleChange('description', e.target.value || undefined)}
              className="input"
              placeholder="Add help text or instructions..."
            />
          </div>

          {/* Required Toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-white/80">Required</label>
            <button
              type="button"
              onClick={() => handleChange('required', !field.required)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                field.required ? 'bg-primary' : 'bg-white/20'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  field.required ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Options (for select, multiselect, radio, checkbox) */}
          {supportsOptions(field.type) && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Options</label>
              <div className="space-y-2">
                {(field.options || []).map((option, optionIndex) => (
                  <div key={optionIndex} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleUpdateOption(optionIndex, e.target.value)}
                      className="input flex-1"
                      placeholder={`Option ${optionIndex + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(optionIndex)}
                      disabled={(field.options?.length || 0) <= 1}
                      className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Remove option"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}

                {/* Add New Option */}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddOption();
                      }
                    }}
                    className="input flex-1"
                    placeholder="Add new option..."
                  />
                  <button
                    type="button"
                    onClick={handleAddOption}
                    disabled={!newOption.trim()}
                    className="btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Validation (for text, textarea, number) */}
          {supportsMinMax(field.type) && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Validation <span className="text-white/40">(optional)</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/40 mb-1">
                    {field.type === 'number' ? 'Minimum Value' : 'Min Length'}
                  </label>
                  <input
                    type="number"
                    value={field.validation?.min ?? ''}
                    onChange={(e) => handleValidationChange('min', e.target.value)}
                    className="input"
                    placeholder="No limit"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-1">
                    {field.type === 'number' ? 'Maximum Value' : 'Max Length'}
                  </label>
                  <input
                    type="number"
                    value={field.validation?.max ?? ''}
                    onChange={(e) => handleValidationChange('max', e.target.value)}
                    className="input"
                    placeholder="No limit"
                    min="0"
                  />
                </div>
              </div>
              {field.type === 'text' && (
                <div className="mt-3">
                  <label className="block text-xs text-white/40 mb-1">Pattern (Regex)</label>
                  <input
                    type="text"
                    value={field.validation?.pattern ?? ''}
                    onChange={(e) => handleValidationChange('pattern', e.target.value)}
                    className="input font-mono text-sm"
                    placeholder="e.g., ^[A-Za-z]+$"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FormFieldEditor;
